<template>
    <div v-if="withMap">
        <map-container :points="readyShipments" :selected-shipments="selectedShipments"
            @shipment-toggled="handleMapSelection" />
    </div>

    <div class="row">
        <div class="d-flex gap-1 justify-content-end align-items-center flex-wrap">
            <button v-if="hasPermission('permission_shipments_waybill') && !isDeleted" type="button"
                class="btn btn-sm btn-outline-secondary" @click="generateXPrint">
                <i class="fa-solid fa-print"></i> {{ $t('button_xprint') }}
            </button>

            <button v-if="hasPermission('permission_shipments_waybill') && !isDeleted" type="button"
                class="btn btn-sm btn-outline-secondary" @click="printWaybill">
                <i class="fa-solid fa-print"></i> {{ $t('button_print_waybill') }}
            </button>

            <button v-if="hasPermission('permission_shipments_label') && !isDeleted" type="button"
                class="btn btn-sm btn-outline-secondary print-barcode" @click="printBarcode">
                <i class="fa fa-fw fa-barcode me-1"></i>
                {{ $t('button_print_barcode') }}
            </button>

            <div v-if="hasPermission('permission_shipments_module_admin')">
                <ChangeStatusModal ref="changeStatusModal" :changeable-shipments-statuses="shipmentStatus"
                    :grouped_users="groupedUsers" :selected-shipments="selectedShipmentIds"
                    @status-updated="handleStatusUpdate"></ChangeStatusModal>
            </div>

            <button v-if="hasPermission('permission_shipments_export')" type="button"
                class="btn btn-sm btn-outline-secondary print-barcode" @click="downloadExcel">
                <i class="fa-solid fa-file-csv"></i>
                {{ $t('button_download_excel') }}
            </button>


            <div class="block-options content-heading">
                <select class="btn-block-option" @change="updatePerPage" v-model="localPerPage">
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="200">200</option>
                </select>
            </div>
        </div>
    </div>

    <!-- Shipments Table -->
    <div class="block-rounded block">
        <div class="block-header block-header-default d-flex justify-content-between align-items-center">
            <div>
                <h3 class="block-title m-0 d-flex align-items-center justify-content-between flex-wrap">
                    <div>
                        {{ $t('title_result') }} <span>{{ shipments.length }}</span>
                        <span v-if="selectedShipments.length > 0" class="text-muted ms-2">
                            ( {{ $t('title_selected') }} {{ selectedShipments.length }} )
                        </span>
                    </div>
                    <div v-if="selectedShipments.length > 0" class="btn-group mt-2 mt-md-0 ms-md-3" role="group">
                        <button v-if="selectedShipments.length !== shipments.length" type="button"
                            class="btn btn-sm btn-alt-primary" @click="toggleSelectAll">
                            <i class="fa-solid fa-list-check me-1"></i> Select All
                        </button>

                        <button v-else type="button" class="btn btn-sm btn-alt-secondary" @click="deselectAll">
                            <i class="fa-solid fa-xmark me-1"></i> Clear All
                        </button>
                    </div>

                </h3>

            </div>
        </div>

        <div class="block-content block-content-full">
            <div class="table-responsive">
                <table class="table-sm table-vcenter fs-sm table-hover table" style="table-layout: auto; width: 100%">
                    <thead>
                        <tr class="text-center fs-6 ">
                            <th style="width: 90px">{{ $t('table_reference') }}</th>
                            <th style="width: 100px">{{ $t('table_client') }}</th>
                            <th style="width: 100px">{{ $t('table_receiver') }}</th>
                            <th style="width: 100px">{{ $t('table_mobile_number') }}</th>
                            <th style="width: 100px">{{ $t('table_area') }}</th>
                            <th style="width: 80px">{{ $t('table_status') }}</th>
                            <th style="width: 70px">{{ $t('table_delivery_price') }}</th>
                            <th style="width: 70px">{{ $t('table_grand_total') }}</th>
                            <th style="width: 100px">{{ $t('table_assigned_to') }}</th>
                            <th style="width: 100px">{{ $t('table_created_at') }}</th>
                            <th style="width: 40px"></th>
                        </tr>
                        <tr class="text-center text-sm h-8">
                            <th>
                                <IconField>
                                    <InputIcon class="pi pi-search" />
                                    <InputText v-model="filters.referenceSearch" type="text" size="small"
                                        class="w-100" />
                                </IconField>
                            </th>
                            <th>
                                <MultiSelect v-model="filters.clientFilter" :placeholder="$t('placeholder.any')"
                                    :options="users" optionLabel="first_name" optionValue="_id" filter
                                    :maxSelectedLabels="3" size="small" class="w-100" :selectionLimit="null">
                                    <template #option="slotProps">
                                        <div class="text-sm flex items-center text-sm gap-2">
                                            <img :alt="slotProps.option.name" :src=slotProps.option.avatar
                                                class="w-8 h-8 rounded-full object-cover" style="width: 32px" />
                                            {{ slotProps.option.first_name }} {{ slotProps.option.last_name }}
                                        </div>
                                    </template>
                                </MultiSelect>
                            </th>
                            <th>
                                <IconField>
                                    <InputIcon class="pi pi-search" />
                                    <InputText v-model="filters.ReceiverNameSearch" type="text" size="small"
                                        class="w-100" />
                                </IconField>
                            </th>
                            <th>
                                <IconField>
                                    <InputIcon class="pi pi-search" />
                                    <InputText type="text" v-model="filters.ReceiverPhoneSearch" size="small"
                                        class="w-100" />
                                </IconField>
                            </th>
                            <th>
                                <MultiSelect v-model="filters.ReceiverAreaFilter" :placeholder="$t('placeholder.any')"
                                    :options="areas" optionValue="_id" optionLabel="city_name_ar" filter
                                    :maxSelectedLabels="3" size="small" class="w-100"
                                    :virtualScrollerOptions="{ itemSize: 36 }">
                                    <template #option="slotProps">
                                        <div class="text-sm">
                                            {{ slotProps.option.governorate.governorate_name_ar }} - {{
                                                slotProps.option.city_name_ar }}
                                        </div>
                                    </template>
                                </MultiSelect>
                            </th>
                            <th>
                                <MultiSelect v-model="filters.StatusFilter" :placeholder="$t('placeholder.any')"
                                    :options="shipmentStatus" optionLabel="title_ar" optionValue="_id"
                                    :maxSelectedLabels="3" size="small" class="w-100">
                                    <template #option="slotProps">
                                        <div class="text-sm">
                                            <span class="badge p-2 text-uppercase"
                                                :class="`bg-${slotProps.option.color}`">
                                                {{ slotProps.option.title_ar }}
                                            </span>
                                        </div>
                                    </template>

                                    <template #selecteditem="slotProps">
                                        <span class="badge p-2 text-uppercase text-sm"
                                            :class="`bg-${slotProps.option.color}`">
                                            {{ slotProps.option.title_ar }}
                                        </span>
                                    </template>
                                </MultiSelect>
                            </th>
                            <th>
                                <IconField>
                                    <InputIcon class="pi pi-search" />
                                    <InputText v-model.number="filters.DeliveryPriceSearch" type="text" size="small"
                                        class="w-100" />
                                </IconField>


                            </th>
                            <th>
                                <IconField>
                                    <InputIcon class="pi pi-search" />
                                    <InputText v-model.number="filters.GrandTotalSearch" type="text" size="small"
                                        class="w-100" />
                                </IconField>

                            </th>
                            <th>
                                <MultiSelect v-model="filters.AssignedToFilter" :options="users"
                                    optionLabel="first_name" optionValue="_id" :placeholder="$t('placeholder.any')"
                                    filter :maxSelectedLabels="3" size="small" class="w-full" :selectionLimit="null">
                                    <template #option="slotProps">
                                        <div class="text-sm flex items-center gap-2">
                                            <img :alt="slotProps.option.name" :src=slotProps.option.avatar
                                                class="w-8 h-8 rounded-full object-cover" style="width: 32px" />
                                            <span>{{ slotProps.option.first_name }} {{ slotProps.option.last_name
                                                }}</span>
                                        </div>
                                    </template>
                                </MultiSelect>
                            </th>
                            <th>
                                <DatePicker v-model="filters.CreatedAtSearch" showIcon fluid iconDisplay="input"
                                    size="small" />
                            </th>
                            <th class="text-center align-middle">
                                <button type="button" class="btn btn-alt-secondary btn-sm" title="Clear Filters"
                                    @click="clearFilters">
                                    <i class="fa fa-solid fa-trash"></i>
                                </button>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="loading" v-for="n in 5" :key="'skeleton-row-' + n">
                            <td v-for="i in 11" :key="'skeleton-cell-' + i" class="text-center">
                                <Skeleton height="2rem" class="my-1" />
                            </td>
                        </tr>
                        <!-- Show message if no shipments and not loading -->
                        <tr v-else-if="shipments.length === 0">
                            <td colspan="11" class="text-muted text-center py-4">{{ $t('message_no_shipments_found') }}
                            </td>
                        </tr>
                        <tr v-else v-for="(shipment, index) in shipments" :key="shipment._id" class="text-center"
                            :class="[
                                shipment.is_collected ? 'table-secondary' : '',
                                isSelected(shipment) ? 'table-primary selected-row' : ''
                            ]" @click="selectShipment(shipment, index, $event)">

                            <td class="text-truncate" style="max-width: 100px">
                                <a :href="`/partner/shipment/details/view/${shipment._id}`"
                                    @click.prevent="navigateTo(shipment._id)" @click.stop>
                                    {{ shipment.reference }}
                                </a>

                            </td>
                            <td class="text-truncate" style="max-width: 120px">
                                <template v-if="shipment.client_data">
                                    <a :href="`/profile/${shipment.client_id}`" @click.stop>
                                        {{ shipment.client_data.first_name }} {{ shipment.client_data.last_name }}
                                    </a>
                                </template>
                                <span v-else class="text-danger">No Client Data</span>
                            </td>

                            <td class="text-truncate" style="max-width: 100px">{{ shipment.receiver_name }}
                                <span v-if="shipment.feedbacks.filter(f => f.is_unreachable).length > 0"
                                    class="badge rounded-circle bg-danger text-white"
                                    style="width: 18px; height: 18px; font-size: 12px; display: inline-flex; align-items: center; justify-content: center;"
                                    :title="shipment.feedbacks
                                        .filter(f => f.is_unreachable)
                                        .map(f => `${new Date(f.created_at).toLocaleDateString()} - ${f.originator?.first_name + ' ' + f.originator?.last_name || 'Unknown'}`)
                                        .join('\n')">
                                    {{shipment.feedbacks.filter(f => f.is_unreachable).length}}
                                </span>
                            </td>
                            <td class="text-truncate" style="max-width: 100px">
                                <span v-for="simNumber in shipment.receiver_sim_number" :key="simNumber" class="d-block">
                                    {{ simNumber }}
                                </span>
                            </td>
                            <td class="text-truncate" style="max-width: 100px">
                                <template v-if="shipment.area_data">
                                    {{ shipment.area_data.city_name_ar }}
                                    <span class="text-muted" v-if="shipment.area_data.governorate?.governorate_name_ar">
                                        , {{ shipment.area_data.governorate.governorate_name_ar }}
                                    </span>
                                </template>
                                <span v-else
                                    class="fs-xs fw-semibold d-inline-block py-1 px-3 rounded-pill bg-secondary text-light">{{
                                        $t('button_no_area') }}</span>
                            </td>
                            <td class="text-truncate" style="max-width: 100px">
                                <span v-if="shipment.deleted_at" class="badge p-2 text-uppercase bg-danger">
                                    Deleted
                                </span>
                                <span v-else class="badge p-2 text-uppercase"
                                    :class="`bg-${shipment.status_data.color}`">
                                    {{ shipment.status_data.title_ar }}
                                </span>
                            </td>
                            <td class="text-truncate" style="max-width: 80px">{{
                                formatNumber(shipment.delivery_price) }} {{
                                    defaultCurrencyAcronym }}</td>
                            <td class="text-truncate" style="max-width: 80px">
                                {{ formatCurrency(shipment.grand_total, shipment.is_reversed) }}
                                {{ defaultCurrencyAcronym }} <!-- Currency symbol outside parentheses -->
                            </td>
                            <td class="text-truncate" style="max-width: 120px">
                                <template v-if="shipment.assigned_to_user?.first_name">
                                    <a :href="`/profile/${shipment.assigned_to}`" @click.stop>
                                        {{ shipment.assigned_to_user.first_name }} {{
                                            shipment.assigned_to_user.last_name }}
                                    </a>
                                </template>
                                <span v-else class="badge bg-info">Unassigned</span>
                            </td>
                            <td class="text-truncate" style="max-width: 100px">{{ formatDate(shipment.created_at) }}
                            </td>
                            <td style="width: auto">
                                <div class="d-flex align-items-center gap-2">
                                    <!-- Modal Trigger (wrap with a span to apply styling if needed) -->
                                    <span @click.stop>
                                        <phone-calls-tracking-modal :shipment="shipment"></phone-calls-tracking-modal>
                                    </span>

                                    <!-- Map Icon -->
                                    <span @click.stop>
                                        <a v-if="shipment.latitude && shipment.longitude"
                                            :href="`https://www.google.com/maps/search/?api=1&query=${shipment.latitude},${shipment.longitude}`"
                                            target="_blank">
                                            <i class="fa fa-map-location-dot fa-lg text-success"></i>
                                        </a>
                                        <i v-else class="fa fa-map-location-dot fa-lg text-muted"
                                            :title="$t('no_location')"></i>
                                    </span>
                                </div>
                            </td>

                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>

    <nav aria-label="Page navigation" v-if="lastPage > 1">
        <ul class="pagination justify-content-end push">
            <!-- Previous Button -->
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <a class="page-link" href="#" @click.prevent="prevPage">
                    <i class="fa fa-angle-left"></i>
                </a>
            </li>

            <!-- Current Page Info -->
            <li class="page-item disabled">
                <span class="page-link">
                    {{ currentPage }}/{{ lastPage }}
                </span>
            </li>

            <!-- Next Button -->
            <li class="page-item" :class="{ disabled: currentPage === lastPage }">
                <a class="page-link" href="#" @click.prevent="nextPage">
                    <i class="fa fa-angle-right"></i>
                </a>
            </li>
        </ul>
    </nav>

</template>
<script>
import MultiSelect from 'primevue/multiselect';
import DatePicker from 'primevue/datepicker';

function debounce(fn, delay) {
    let timeout;
    return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), delay);
    };
}

export default {
    props: {
        shipments: {
            type: Array,
            default: () => []
        },
        currentPage: Number,
        lastPage: Number,
        perPage: {
            type: Number,
            default: 50
        },
        total: Number,
        loading: {
            type: Boolean,
            default: true
        },
        shipmentStatus: {
            type: Array,
            default: () => []
        },
        initialSelectedShipments: {
            type: Array,
            default: () => []
        },
        withMap: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            selectAll: false,
            selectedShipments: [], // Local state for selected shipments
            lastSelectedIndex: null, // for shift-click range
            filters: {
                referenceSearch: null,
                clientFilter: [],
                ReceiverNameSearch: null,
                ReceiverPhoneSearch: null,
                ReceiverAreaFilter: [],
                StatusFilter: [],
                DeliveryPriceSearch: null,
                GrandTotalSearch: null,
                AssignedToFilter: [],
                CreatedAtSearch: null
            },
            localPerPage: this.perPage,
            areas: [],
            users: [],
            groupedUsers: [],
            isExporting: false,
            printing: false
        }
    },



    mounted() {
        this.fetchAreas();
        this.fetchUsers();
        this.fetchUsersGroupedByRole();
    },

    watch: {
        filters: {
            handler: debounce(function (newFilters) {
                // Reset to first page when filters change
                this.$emit('filters-updated', {
                    ...newFilters,
                    page: 1, // Always reset to page 1
                    perPage: this.localPerPage
                });
            }, 500),
            deep: true,
            immediate: false
        },
        perPage(newVal) {
            this.localPerPage = newVal;
        },
        initialSelectedShipments: {
            handler(newVal) {
                // Sync parent's selection with local state
                this.selectedShipments = [...newVal];
            },
            immediate: true
        },
    },

    computed: {
        selectedShipmentIds() {
            return this.selectedShipments;
        },

        readyShipments() {
            return this.shipments.filter(s =>
                s.is_ready &&
                !s.is_delivered &&
                s.latitude &&
                s.longitude
            );
        },

        pages() {
            const pages = []
            // Show max 5 pages around current page
            let start = Math.max(1, this.filters.page - 2)
            let end = Math.min(this.lastPage, this.filters.page + 2)

            // Adjust if we're near the start or end
            if (this.filters.page <= 3) {
                end = Math.min(5, this.lastPage)
            }
            if (this.filters.page >= this.lastPage - 2) {
                start = Math.max(this.lastPage - 4, 1)
            }

            for (let i = start; i <= end; i++) {
                pages.push(i)
            }
            return pages
        },

        visiblePages() {
            const range = [];
            const current = this.filters.page;
            const total = this.totalPages;

            // Always show first page
            if (current > 3) range.push(1);

            // Show pages around current
            for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
                range.push(i);
            }

            // Always show last page if not already shown
            if (current < total - 2) range.push(total);

            return range;
        }
    },

    methods: {
        formatCurrency(value, isReversed = false) {
            if (value === null || value === undefined) return "-";

            const formattedValue = this.formatNumber(Math.abs(value));

            // Use parentheses ONLY if reversed (regardless of negative value)
            return isReversed
                ? `(${formattedValue})`
                : formattedValue;
        },

        // Your existing formatNumber (kept as-is)
        formatNumber(value) {
            return new Intl.NumberFormat().format(value);
        },
        clearFilters() {
            this.filters = {
                referenceSearch: null,
                clientFilter: [],
                ReceiverNameSearch: null,
                ReceiverPhoneSearch: null,
                ReceiverAreaFilter: [],
                StatusFilter: [],
                DeliveryPriceSearch: null,
                GrandTotalSearch: null,
                AssignedToFilter: [],
                CreatedAtSearch: null
            };
            this.currentPage = 1;
        },

        updatePerPage() {
            this.$emit('filters-updated', {
                ...this.filters,
                page: 1, // Reset to first page when changing items per page
                perPage: this.localPerPage
            });
        },

        selectShipment(shipment, index, event) {
            let updatedSelection = [...this.selectedShipments];
            const shipmentId = shipment._id;

            if (event.shiftKey && this.lastSelectedIndex !== null) {
                // Shift-click selection logic
                const start = Math.min(index, this.lastSelectedIndex);
                const end = Math.max(index, this.lastSelectedIndex);

                for (let i = start; i <= end; i++) {
                    const s = this.paginatedShipments[i];
                    const sId = s._id;
                    if (!updatedSelection.includes(sId)) {
                        updatedSelection.push(sId);
                    }
                }
            } else {
                // Single click selection logic
                const existingIndex = updatedSelection.indexOf(shipmentId);
                if (existingIndex !== -1) {
                    updatedSelection.splice(existingIndex, 1);
                } else {
                    updatedSelection.push(shipmentId);
                }
                this.lastSelectedIndex = index;
            }

            // Update local state which will propagate to map
            this.selectedShipments = updatedSelection;
        },

        handleMapSelection(selectedIds) {
            this.selectedShipments = selectedIds;
        },

        isSelected(shipment) {
            return this.selectedShipments.includes(shipment._id);
        },

        toggleSelectAll() {
            let newSelection;
            if (this.selectedShipments.length === this.shipments.length) {
                newSelection = [];
            } else {
                newSelection = this.shipments.map(s => s._id);
            }
            this.updateSelectedShipments(newSelection);
        },

        deselectAll() {
            this.updateSelectedShipments([]);
        },

        updateSelectedShipments(updatedSelection) {
            this.selectedShipments = updatedSelection;
        },

        async hasPermission(permission) {
            try {
                const response = await axios.get(`/partner-users/check-permission/${permission}`);
                return response.data.hasPermission;
            } catch (error) {
                console.error('Error checking permission:', error);
                return false;
            }
        },

        fetchUsersGroupedByRole() {
            axios.get('/partner-users/get-auth-partner-users-grouped')
                .then(response => {
                    this.groupedUsers = response.data;
                })
                .catch(error => {
                    console.error('Error fetching users:', error);
                });
        },

        goToPage(page) {
            if (page < 1 || page > this.lastPage || page === this.currentPage) return;
            this.$emit('filters-updated', {
                ...this.filters,
                page: page,
                perPage: this.localPerPage
            });
        },
        prevPage() {
            if (this.currentPage > 1) {
                this.goToPage(this.currentPage - 1);
            }
        },
        nextPage() {
            if (this.currentPage < this.lastPage) {
                this.goToPage(this.currentPage + 1);
            }
        },

        emitFilters() {
            this.$emit('filters-updated', {
                ...this.filters,
                page: this.filters.page,
                perPage: this.filters.perPage
            });
        },

        toggleStatusFilter(statusId) {
            const index = this.filters.status.indexOf(statusId);
            if (index === -1) {
                this.filters.status.push(statusId);
            } else {
                this.filters.status.splice(index, 1);
            }
            this.currentPage = 1;
        },

        navigateTo(url) {
            window.location.href = url;
        },

        showSimActions(simNumber) {
            // Implement SIM number actions (WhatsApp, call, copy)
            if (confirm(`Actions for ${simNumber}:\n1. WhatsApp\n2. Call\n3. Copy`)) {
                // Handle action
            }
        },

        formatNumber(number) {
            return new Intl.NumberFormat().format(number);
        },

        formatDate(date) {
            return new Date(date).toLocaleString();
        },

        printWaybill() {
            One.layout('header_loader_on');
            if (this.selectedShipments.length === 0) {
                Swal.fire("Warning", "No shipments selected", "warning");
                return;
            }

            axios.post('/shipments/print/waybill', {
                shipment_ids: this.selectedShipments
            }, {
                headers: {
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                }
            }).then(response => {
                // Create a hidden iframe
                const iframe = document.createElement('iframe');
                iframe.style.position = 'fixed';
                iframe.style.right = '0';
                iframe.style.bottom = '0';
                iframe.style.width = '0';
                iframe.style.height = '0';
                iframe.style.border = '0';

                document.body.appendChild(iframe);

                const doc = iframe.contentWindow || iframe.contentDocument;
                const content = doc.document || doc;

                content.open();
                content.write(response.data);
                content.close();

                // Listen for print start and end events
                iframe.contentWindow.onbeforeprint = () => {
                    console.log("Print dialog opened");
                };

                iframe.contentWindow.onafterprint = () => {
                    console.log("Print dialog closed");
                    // Optional: clean up only after the print dialog is closed
                    setTimeout(() => {
                        document.body.removeChild(iframe);
                    }, 1000);
                };

                iframe.onload = () => {
                    iframe.contentWindow.focus();
                    iframe.contentWindow.print();
                };

            }).catch(error => {
                console.error("Print Error:", error);
                Swal.fire("Error", "Failed to print waybill.", "error");
            }).finally(() => {
                One.layout('header_loader_off');
            });
        },

        generateXPrint() {
            One.layout('header_loader_on');

            if (this.selectedShipments.length === 0) {
                Swal.fire("Warning", "No shipments selected", "warning");
                One.layout('header_loader_off');
                return;
            }

            axios.post('/shipments/print/xprint', {
                shipment_ids: this.selectedShipments
            }, {
                headers: {
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                }
            })
                .then(response => {
                    const htmlContent = response.data;

                    // Check if xprint object is available
                    if (typeof xprint !== 'undefined' && typeof xprint.printHtml === 'function') {
                        xprint.printHtml(htmlContent,
                            () => {
                                console.log("Print successful");
                                Swal.fire("Success", "Printed successfully.", "success");
                            },
                            (err) => {
                                console.error("XPrint failed:", err);
                                Swal.fire("Error", "XPrint failed to print.", "error");
                            }
                        );
                    } else {
                        // Fallback for desktop/web environments
                        const printWindow = window.open('', '_blank');
                        printWindow.document.open();
                        printWindow.document.write(htmlContent);
                        printWindow.document.close();
                        printWindow.focus();
                        printWindow.print();
                    }
                })
                .catch(error => {
                    console.error("Print Error:", error);
                    Swal.fire("Error", "Failed to print waybill.", "error");
                })
                .finally(() => {
                    One.layout('header_loader_off');
                });
        },

        printBarcode() {
            if (this.selectedShipments.length === 0) {
                alert('Please select at least one shipment');
                return;
            }
            // Implement print functionality
        },

        downloadPDF() {
            if (this.selectedShipments.length === 0) {
                alert('Please select at least one shipment');
                return;
            }
            // Implement download functionality
        },

        fetchAreas() {
            $.ajax({
                url: '/get-all-areas',  // Replace with your endpoint
                method: 'GET',
                success: (data) => {
                    this.areas = data;
                    this.$nextTick(() => {
                        $('#AreaFilter').selectpicker('refresh');
                    });
                },
                error: (xhr, status, error) => {
                    console.error('Error fetching areas:', error);
                }
            });
        },

        fetchUsers() {
            $.ajax({
                url: '/get-all-clients',  // Replace with your endpoint
                method: 'GET',
                success: (data) => {
                    this.users = data;
                },
                error: (xhr, status, error) => {
                    console.error('Error fetching users:', error);
                }
            });
        },

        handleStatusUpdate(updatedData) {
            const updatedShipments = updatedData.data.shipments;
            if (updatedShipments && updatedShipments.length > 0) {
                updatedShipments.forEach((updatedShipment) => {
                    const shipmentIndex = this.shipments.findIndex(shipment => shipment._id === updatedShipment.id);
                    if (shipmentIndex !== -1) {
                        if (updatedShipment.status) {
                            this.shipments[shipmentIndex].status_id = updatedShipment.status.id;
                            this.shipments[shipmentIndex].status_data = { ...updatedShipment.status };
                        }
                        if (updatedShipment.assigned_user) {
                            this.shipments[shipmentIndex].assigned_to_user = { ...updatedShipment.assigned_user };
                        }
                    }
                });
            }
        },

        async downloadExcel() {
            this.isExporting = true;

            if (this.selectedShipments.length > 0) {
                try {
                    const response = await axios.post(
                        '/shipments/export',
                        { shipmentsIds: this.selectedShipments },
                        { responseType: 'blob' }
                    );

                    // Create your custom filename
                    const currentDate = new Date();
                    const formattedDate = currentDate.toISOString().slice(0, 10); // YYYY-MM-DD
                    const formattedTime = currentDate.toTimeString().slice(0, 8).replace(/:/g, '-'); // HH-MM-SS

                    // Set your custom filename pattern here
                    const customFilename = `Shipment_Export_${formattedDate}_${formattedTime}.xlsx`;

                    // Create download link
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', customFilename); // Use your custom filename
                    document.body.appendChild(link);
                    link.click();

                    // Clean up
                    link.remove();
                    window.URL.revokeObjectURL(url);

                } catch (error) {
                    console.error('Error exporting shipments:', error);
                    Swal.fire({
                        title: "Error!",
                        text: this.$t('message_export_failed'),
                        icon: "error",
                    });
                } finally {
                    this.isExporting = false;
                }
            } else {
                Swal.fire({
                    title: "Warning!",
                    text: this.$t('message_no_shipments_selected'),
                    icon: "warning",
                });
                this.isExporting = false;
            }
        },
    }
}
</script>


<style scoped>
.skeleton-loader {
    height: 20px;
    background-color: #f0f0f0;
    border-radius: 4px;
    margin: 5px 0;
    animation: pulse 1.5s infinite ease-in-out;
}

@keyframes pulse {
    0% {
        opacity: 0.6;
    }

    50% {
        opacity: 0.3;
    }

    100% {
        opacity: 0.6;
    }
}

table {
    table-layout: auto;
    width: 100%;
}

td {
    white-space: normal !important;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 180px;
    /* Adjust as needed */
    word-break: break-word;
}

tr {
    cursor: pointer;
}

.status-badge:hover {
    opacity: 0.8;
}

.client_sim_number span {
    cursor: pointer;
}

.client_sim_number span:hover {
    text-decoration: underline;
}

.dropdown-menu {
    max-height: 250px;
    /* Set your desired maximum height */
    overflow-y: auto;
    /* Enable vertical scrolling when content overflows */
}

.table-active {
    background-color: #0056b3 !important;
    /* Optional: change the background for active state */
}

.selected-row {
    background-color: #007bff !important;
    /* Change to the color you want */
    color: white;
    /* Optional: change text color for contrast */
}
</style>